import React, { useState, useEffect, useCallback } from 'react';
import {
  Table, Card, Button, Tag, Space, Typography, Select, DatePicker,
  message, Tooltip, Badge, Modal, Descriptions, Switch, AutoComplete, Input
} from 'antd';
import {
  ReloadOutlined, FilterOutlined, DownloadOutlined,
  InfoCircleOutlined, CheckCircleOutlined, ClockCircleOutlined,
  ExclamationCircleOutlined, SettingOutlined,
  FireOutlined, DashboardOutlined, SignalFilled, ThunderboltOutlined
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import { useAuth } from '../contexts/AuthContext';
import logger from '../services/logger';
import { safeFormatTimestamp } from '../utils/timezone';

const { Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

// Operasyonel durum config
const OP_STATUS_CONFIG = {
  idle: { label: 'Boşta', color: 'default', icon: <ClockCircleOutlined /> },
  running: { label: 'Çalışıyor', color: 'processing', icon: <CheckCircleOutlined /> },
  paused: { label: 'Duraklatıldı', color: 'warning', icon: <ClockCircleOutlined /> },
  error: { label: 'Hata', color: 'error', icon: <ExclamationCircleOutlined /> },
  maintenance: { label: 'Bakım', color: 'warning', icon: <SettingOutlined /> }
};

const Telemetry = () => {
  const [telemetryData, setTelemetryData] = useState([]);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({});
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedTelemetry, setSelectedTelemetry] = useState(null);
  const [filters, setFilters] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(10);

  const [deviceSearchText, setDeviceSearchText] = useState('');
  const [deviceOptions, setDeviceOptions] = useState([]);

  const { user } = useAuth();

  const fetchTelemetryData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        limit: pagination.pageSize,
        offset: (pagination.current - 1) * pagination.pageSize
      };
      if (filters.device_id) params.device_id = filters.device_id;
      if (filters.operational_status) params.operational_status = filters.operational_status;
      if (filters.start_time) params.start_time = filters.start_time;
      if (filters.end_time) params.end_time = filters.end_time;
      if (searchTerm) params.search = searchTerm;

      const response = await axios.get('/api/v1/telemetry/all', { params });

      if (response.data && response.data.telemetry_data) {
        setTelemetryData(response.data.telemetry_data);
        setPagination(prev => ({
          ...prev,
          total: response.data.total_count || 0
        }));
      } else {
        setTelemetryData([]);
      }
    } catch (error) {
      logger.error('Error fetching telemetry data:', error);
      message.error('Telemetri verileri yüklenirken hata oluştu');
      setTelemetryData([]);
    } finally {
      setLoading(false);
    }
  }, [pagination.pageSize, pagination.current, filters, searchTerm]);

  const fetchDevices = useCallback(async () => {
    try {
      const response = await axios.get('/api/v1/devices');
      setDevices(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      logger.error('Devices fetch error:', error);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const params = {};
      if (filters.start_time) params.start_time = filters.start_time;
      if (filters.end_time) params.end_time = filters.end_time;
      const response = await axios.get('/api/v1/telemetry/stats', { params });
      if (response.data) setStats(response.data);
    } catch (error) {
      logger.error('Stats fetch error:', error);
      setStats({});
    }
  }, [filters.start_time, filters.end_time]);

  useEffect(() => {
    fetchTelemetryData();
    fetchDevices();
    fetchStats();
  }, [fetchTelemetryData, fetchDevices, fetchStats]);

  // Cihaz arama (debounced)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (deviceSearchText && deviceSearchText.trim().length >= 1) {
        searchDevices(deviceSearchText);
      } else {
        setDeviceOptions([]);
      }
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [deviceSearchText]);

  const searchDevices = async (searchText) => {
    try {
      const response = await axios.get('/api/v1/devices', {
        params: { search: searchText, limit: 20 }
      });
      if (response.data && Array.isArray(response.data)) {
        setDeviceOptions(response.data.map(device => ({
          value: device.id.toString(),
          label: `${device.name} (${device.mac_address})`
        })));
      }
    } catch (error) {
      logger.error('Device search error:', error);
    }
  };

  // Auto-refresh
  useEffect(() => {
    let intervalId;
    if (autoRefresh) {
      intervalId = setInterval(() => {
        fetchTelemetryData();
        fetchStats();
      }, refreshInterval * 1000);
    }
    return () => { if (intervalId) clearInterval(intervalId); };
  }, [autoRefresh, refreshInterval, fetchTelemetryData, fetchStats]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleDateRangeChange = (dates) => {
    setFilters(prev => ({
      ...prev,
      start_time: dates && dates[0] ? dates[0].toISOString() : null,
      end_time: dates && dates[1] ? dates[1].toISOString() : null
    }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleClearFilters = () => {
    setFilters({});
    setSearchTerm('');
    setDeviceSearchText('');
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const getOpStatusConfig = (status) => {
    return OP_STATUS_CONFIG[status] || { label: status, color: 'default', icon: <InfoCircleOutlined /> };
  };

  const columns = [
    {
      title: 'Zaman',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 170,
      fixed: 'left',
      render: (ts) => safeFormatTimestamp(ts),
      sorter: (a, b) => new Date(a.timestamp) - new Date(b.timestamp),
    },
    {
      title: 'Cihaz',
      dataIndex: 'device_name',
      key: 'device_name',
      width: 180,
      render: (text, record) => (
        <div>
          <Text strong>{text}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 11 }}>{record.device_mac}</Text>
        </div>
      ),
    },
    {
      title: 'Durum',
      dataIndex: ['status', 'operational_status'],
      key: 'operational_status',
      width: 130,
      render: (status) => {
        const c = getOpStatusConfig(status);
        return <Tag color={c.color} icon={c.icon}>{c.label}</Tag>;
      },
    },
    {
      title: 'Su',
      dataIndex: ['sensors', 'water_temperature'],
      key: 'water_temperature',
      width: 80,
      render: (temp) => temp != null ? `${temp}°C` : '-',
    },
    {
      title: 'Ortam',
      dataIndex: ['sensors', 'ambient_temperature'],
      key: 'ambient_temperature',
      width: 80,
      render: (temp) => temp != null ? `${temp}°C` : '-',
    },
    {
      title: 'Sinyal',
      dataIndex: ['sensors', 'signal_strength'],
      key: 'signal_strength',
      width: 90,
      render: (s) => s != null ? `${s} dBm` : '-',
    },
    {
      title: 'Akış',
      dataIndex: ['sensors', 'water_flow'],
      key: 'water_flow',
      width: 90,
      render: (f) => f != null ? `${f} L/min` : '-',
    },
    {
      title: 'Atış',
      dataIndex: ['device_info', 'total_shots'],
      key: 'total_shots',
      width: 70,
      render: (s) => s ?? '-',
    },
    {
      title: '',
      key: 'actions',
      width: 50,
      align: 'center',
      render: (_, record) => (
        <Button
          type="text"
          size="small"
          icon={<InfoCircleOutlined />}
          onClick={() => { setSelectedTelemetry(record); setDetailModalVisible(true); }}
        />
      ),
    },
  ];

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Typography.Title level={2}>
          <DashboardOutlined /> Telemetri
        </Typography.Title>
      </div>

      {/* Stats Summary */}
      <Card size="small" style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
            <Space size="large" wrap>
              <span>
                <DashboardOutlined style={{ color: '#1890ff', marginRight: 6 }} />
                <Text style={{ marginRight: 4 }}>Toplam Veri</Text>
                <Text strong style={{ fontSize: 18 }}>{stats.total || 0}</Text>
              </span>
              <span>
                <Badge status="success" />
                <Text style={{ marginRight: 4 }}>Çalışan</Text>
                <Text strong style={{ color: '#52c41a', fontSize: 18 }}>{stats.running || 0}</Text>
              </span>
              <span>
                <Badge status="processing" />
                <Text style={{ marginRight: 4 }}>Bekleyen</Text>
                <Text strong style={{ fontSize: 18 }}>{stats.idle || 0}</Text>
              </span>
              {(stats.error || 0) > 0 && (
                <span>
                  <Badge status="error" />
                  <Text style={{ marginRight: 4 }}>Hata</Text>
                  <Text strong style={{ color: '#f5222d', fontSize: 18 }}>{stats.error}</Text>
                </span>
              )}
            </Space>
          </div>
        </Card>

      {/* Filters and Table */}
      <Card style={{ marginBottom: 24 }}>
        <div style={{ marginBottom: 16, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <AutoComplete
            placeholder="Cihaz ara..."
            style={{ width: 200 }}
            options={deviceOptions}
            onSearch={setDeviceSearchText}
            onSelect={(value) => handleFilterChange('device_id', parseInt(value))}
            onChange={(value) => { if (!value) { handleFilterChange('device_id', null); setDeviceSearchText(''); } }}
            allowClear
            filterOption={false}
            value={deviceSearchText}
          />
          <Select
            placeholder="Op. Durum"
            value={filters.operational_status}
            onChange={(value) => handleFilterChange('operational_status', value)}
            style={{ width: 150 }}
            allowClear
          >
            {Object.entries(OP_STATUS_CONFIG).map(([key, cfg]) => (
              <Option key={key} value={key}>{cfg.label}</Option>
            ))}
          </Select>
          <RangePicker
            placeholder={['Başlangıç', 'Bitiş']}
            onChange={handleDateRangeChange}
            style={{ width: 280 }}
            format="YYYY-MM-DD"
          />
          <Input
            placeholder="Cihaz adı veya MAC ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onPressEnter={() => fetchTelemetryData()}
            style={{ width: 220 }}
            allowClear
          />
        </div>

        {/* Action Buttons */}
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <Space wrap>
            <Button icon={<FilterOutlined />} onClick={handleClearFilters} size="small">Temizle</Button>
            <Button icon={<ReloadOutlined />} onClick={() => { fetchTelemetryData(); fetchStats(); }} loading={loading} size="small">Yenile</Button>
          </Space>
          <Space>
            <Switch checked={autoRefresh} onChange={setAutoRefresh} checkedChildren="Auto" unCheckedChildren="Auto" size="small" />
            {autoRefresh && (
              <>
                <Select value={refreshInterval} onChange={setRefreshInterval} style={{ width: 80 }} size="small">
                  <Option value={5}>5 sn</Option>
                  <Option value={10}>10 sn</Option>
                  <Option value={30}>30 sn</Option>
                  <Option value={60}>1 dk</Option>
                </Select>
                <Tag color="green" icon={<ReloadOutlined spin />}>Otomatik</Tag>
              </>
            )}
          </Space>
        </div>

        {/* Table */}
        <Table
          columns={columns}
          dataSource={telemetryData}
          rowKey={(record) => `${record.device_id}-${record.timestamp}`}
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showTotal: (total) => `Toplam ${total} kayıt`,
          }}
          onChange={(p) => setPagination(p)}
          scroll={{ x: 1100 }}
          size="small"
        />
      </Card>

      {/* Detail Modal */}
      <Modal
        title="Telemetri Detayı"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={null}
        width={700}
      >
        {selectedTelemetry && (
          <Descriptions bordered column={2} size="small">
            <Descriptions.Item label="Cihaz">{selectedTelemetry.device_name}</Descriptions.Item>
            <Descriptions.Item label="MAC">{selectedTelemetry.device_mac}</Descriptions.Item>
            <Descriptions.Item label="Zaman">{safeFormatTimestamp(selectedTelemetry.timestamp)}</Descriptions.Item>
            <Descriptions.Item label="Durum">
              {(() => { const c = getOpStatusConfig(selectedTelemetry.status?.operational_status); return <Tag color={c.color} icon={c.icon}>{c.label}</Tag>; })()}
            </Descriptions.Item>
            <Descriptions.Item label="Durum Özeti" span={2}>
              {selectedTelemetry.status?.status_summary || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Su Sıcaklığı">
              {selectedTelemetry.sensors?.water_temperature != null ? `${selectedTelemetry.sensors.water_temperature}°C` : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Ortam Sıcaklığı">
              {selectedTelemetry.sensors?.ambient_temperature != null ? `${selectedTelemetry.sensors.ambient_temperature}°C` : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Sinyal Gücü">
              {selectedTelemetry.sensors?.signal_strength != null ? `${selectedTelemetry.sensors.signal_strength} dBm` : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Su Akışı">
              {selectedTelemetry.sensors?.water_flow != null ? `${selectedTelemetry.sensors.water_flow} L/min` : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Seri Numarası">{selectedTelemetry.device_info?.serial_number || '-'}</Descriptions.Item>
            <Descriptions.Item label="Model">{selectedTelemetry.device_info?.model || '-'}</Descriptions.Item>
            <Descriptions.Item label="Firmware">{selectedTelemetry.device_info?.firmware_version || '-'}</Descriptions.Item>
            <Descriptions.Item label="Toplam Atış">{selectedTelemetry.device_info?.total_shots ?? '-'}</Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default Telemetry;
